import React, { useState } from "react";
import { FileUploaderDropContainer, FileUploaderItem } from "@carbon/react";
import i18next from "../../../i18n";
import { PurchaseRequestFilePayload } from "../../../types/purchaseRequestV2.type";

type Props = {
  files: PurchaseRequestFilePayload[];
  uploadFile: (file: File) => Promise<PurchaseRequestFilePayload>;
  onChange: (next: PurchaseRequestFilePayload[]) => void;
  onError?: (message: string) => void;
};

/**
 * Per-item attachment picker, rebuilt on Carbon's `FileUploaderDropContainer`/`FileUploaderItem`
 * (the same pattern earlier flows use). The upload itself stays imperative and bridged into form
 * state exactly as before — `uploadFile` (an S3 POST) runs per picked file, the fulfilled results
 * are appended, and a per-item files array is handed back through `onChange`; nothing about the
 * multi-file upload/partial-failure handling changed, only the widget.
 */
const ItemFilePicker: React.FC<Props> = ({
  files,
  uploadFile,
  onChange,
  onError,
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleAddFiles = async (
    _event: unknown,
    { addedFiles }: { addedFiles: File[] }
  ) => {
    if (addedFiles.length === 0) return;
    setIsUploading(true);
    try {
      const results = await Promise.allSettled(addedFiles.map(uploadFile));
      const uploaded = results
        .filter(
          (r): r is PromiseFulfilledResult<PurchaseRequestFilePayload> =>
            r.status === "fulfilled"
        )
        .map((r) => r.value);
      const failures = results.filter((r) => r.status === "rejected");
      if (uploaded.length > 0) {
        onChange([...files, ...uploaded]);
      }
      if (failures.length > 0 && onError) {
        onError(
          i18next.t("purchaseRequests.items.fileUploadFailed", {
            count: failures.length,
          })
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = (idx: number) => {
    onChange(files.filter((_, i) => i !== idx));
  };

  return (
    <div className="new-request-item__files">
      <p className="cds--label">
        {i18next.t("purchaseRequests.items.filesLabel")}
      </p>
      <FileUploaderDropContainer
        multiple
        labelText={
          isUploading
            ? i18next.t("cta.uploading")
            : i18next.t("purchaseRequests.items.attachFiles")
        }
        onAddFiles={handleAddFiles}
      />
      {files.length === 0 ? (
        <p className="new-request-item__files-hint">
          {i18next.t("purchaseRequests.items.filesHint")}
        </p>
      ) : (
        <div className="new-request-item__files-list">
          {files.map((f, i) => (
            <FileUploaderItem
              key={f.clientId ?? f.fileURL ?? `idx-${i}`}
              name={f.name || `file-${i + 1}`}
              status="complete"
              onDelete={() => handleRemove(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ItemFilePicker;
